/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { memo } from "react";
import {
  EuiCallOut,
  EuiListGroup,
  EuiListGroupItem,
  EuiLoadingSpinner,
  EuiPopover,
} from "@inscopix/ideas-eui";
import { gql } from "@apollo/client";
import { useCellEditorToolVersionQuery } from "graphql/_Types";
import { useDataTableContext } from "../store/DataTableProvider";
import { ToolSpec } from "types/json-schemas/type_guards";

gql`
  query CellEditorToolVersion($toolVersionId: Int!) {
    toolVersion: toolVersionById(id: $toolVersionId) {
      id
      compatibleVersions {
        nodes {
          id
          version
          toolSpec
          credits
          status
        }
      }
    }
  }
`;

const styles = {
  popover: css`
    & {
      height: 100%;
      width: 100%;
      display: flex;
      align-items: center;
    }
  `,
  panel: css`
    max-height: max(200px, 50vh);
    overflow-y: scroll;
    align-items: center;
  `,
};

interface CellEditorToolVersionProps {
  rowId: string;
  selectedToolVersion: { id: number; version: string } | null;
  stopEditing: (cancel?: boolean) => void;
  tableId: string;
}

/** Cell editor for cells in the tool version column of an analysis table */
const CellEditorToolVersion = ({
  rowId,
  selectedToolVersion,
  stopEditing,
  tableId,
}: CellEditorToolVersionProps) => {
  const selectedTable = useDataTableContext((s) =>
    s.tables.find(({ id }) => id === tableId),
  );
  const tableToolVersion = selectedTable?.toolVersion;
  const updateAnalysisTableRow = useDataTableContext(
    (s) => s.updateAnalysisTableRow,
  );
  const { data, loading } = useCellEditorToolVersionQuery({
    fetchPolicy: "cache-first",
    variables: {
      toolVersionId: tableToolVersion?.id ?? 0,
    },
  });
  const compatibleToolVersions = data?.toolVersion?.compatibleVersions.nodes;

  return (
    <EuiPopover
      anchorPosition="downCenter"
      button={
        <span style={{ padding: "0px 11px" }}>
          {selectedToolVersion?.version ?? tableToolVersion?.version}
        </span>
      }
      isOpen
      panelPaddingSize="none"
      css={styles.popover}
      closePopover={stopEditing}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          e.preventDefault();
          stopEditing(true);
        }
      }}
      display="block"
      repositionOnScroll={false}
    >
      <EuiListGroup css={styles.panel} maxWidth="100%">
        {(() => {
          if (loading) {
            return <EuiLoadingSpinner />;
          } else if (compatibleToolVersions === undefined) {
            return (
              <EuiCallOut
                title="Sorry, there was an error"
                color="danger"
                iconType="error"
              >
                <p>Failed to fetch tool versions.</p>
              </EuiCallOut>
            );
          } else {
            return compatibleToolVersions.map(
              ({ id, version, toolSpec, credits, status }) => (
                <EuiListGroupItem
                  id={id.toString()}
                  key={id}
                  iconType={id === selectedToolVersion?.id ? "check" : "empty"}
                  label={version}
                  onClick={() => {
                    if (
                      selectedTable !== undefined &&
                      id !== selectedToolVersion?.id
                    ) {
                      void updateAnalysisTableRow({
                        tableId: selectedTable.id,
                        rowId,
                        toolVersion: {
                          id,
                          version,
                          resources: (toolSpec as unknown as ToolSpec)
                            .resources,
                          credits: credits ?? 0,
                          status,
                        },
                      });
                      stopEditing();
                    }
                  }}
                />
              ),
            );
          }
        })()}
      </EuiListGroup>
    </EuiPopover>
  );
};

export const CellEditorToolVersionMemo = memo(CellEditorToolVersion);
